import * as m from '@staiter/i18n';
import type { ReconciliationPreviewLine } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Check, Eye, Pencil, Plus, ShieldAlert, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EntityRefCell, ListPageShell, PageHeader, Table } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Dialog, Input, Select } from '~/components/primitives';
import { DocStatusBadge, type DocStatusValue } from '~/components/status';
import { CancelPreviewViewer } from '~/components/stock';
import { fetchWarehouses } from '~/lib/api/inventory';
import { fetchStockCancelApprovers } from '~/lib/api/stock-entries';
import {
  SR_VARIANCE_REASONS,
  cancelPreviewStockReconciliation,
  cancelStockReconciliation,
  fetchStockReconciliationDocs,
  previewStockReconciliation,
  setStockReconciliationReasons,
  submitStockReconciliation,
} from '~/lib/api/stock-reconciliation';
import { formatDate, formatDecimal, formatMoney } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { getRoleLabel } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.stock-reconciliation.module.css';

/**
 * Наибольшее отклонение по строкам — считается ЗДЕСЬ, а не приходит с сервера.
 *
 * Прежняя схема ждала поле `max_single_variance`, которого ручка не отдаёт
 * (одно из восьми расхождений, вскрытых гейтом C). Значение выводится из тех
 * же строк, что уже лежат в таблице ниже — без второго источника правды.
 */
function maxVarianceOf(lines: ReadonlyArray<{ variance_qty: string }>): string {
  /*
   * Раунд 11 гейта C — самое крупное отклонение возвращается СО ЗНАКОМ.
   *
   * Модуль брался раньше не для сравнения, а для вывода: сводка показывала
   * «20» там, где в таблице стояло «−20». Знак здесь — не оформление, а
   * содержание: недостача и излишек — разные события с разными последствиями.
   * Модуль остаётся в СРАВНЕНИИ — самое крупное по величине, но выводится как есть.
   */
  let extreme = 0;
  for (const l of lines) {
    const v = Number(l.variance_qty);
    if (Number.isFinite(v) && Math.abs(v) > Math.abs(extreme)) extreme = v;
  }
  return String(extreme);
}

/** Якорь предупреждения о незаполненной причине — на него ссылается кнопка. */
const REASONS_ALERT_ID = 'sr-preview-reasons-missing';

export const Route = createFileRoute('/_auth/stock-reconciliation/')({
  staticData: { minMainWidth: 907 },
  component: StockReconciliationPage,
});

/** F53 (WHS-F21): причины отклонения — тот же словарь, что у мастера создания. */
function varianceReasonOptions() {
  return [
    { value: '', label: m.route_sr_reason_none() },
    ...SR_VARIANCE_REASONS.map((reason) => ({
      value: reason,
      label: m.route_sr_new_variance_label({ reason }),
    })),
  ];
}

/** F53 (WHS-F22): метод оценки словами; неизвестный код — как есть. */
function valuationMethodLabel(method: string | undefined): string {
  if (method === 'moving_avg') return m.valuation_method_moving_avg();
  if (method === 'fifo') return m.valuation_method_fifo();
  if (method === 'lifo') return m.valuation_method_lifo();
  return method ?? '—';
}

function severityLabel(severity: ReconciliationPreviewLine['severity']): string {
  return m.route_sr_severity_label({ severity });
}

function StockReconciliationPage(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelApprover, setCancelApprover] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const docsQuery = useQuery({
    queryKey: ['stock-reconciliation', 'docs'],
    queryFn: () => fetchStockReconciliationDocs(),
    staleTime: 20_000,
  });
  const docsState = useListPageState(docsQuery);

  // FE-92 Sprint Q+: client-side warehouse_id → name denorm
  // (fallback пока BE-D не зашит).
  const warehousesQuery = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => fetchWarehouses(),
    staleTime: 5 * 60_000,
  });
  const warehouseMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehousesQuery.data?.items ?? []) {
      map.set(w.id, w.name || w.code);
    }
    return map;
  }, [warehousesQuery.data]);

  /*
   * F53 (WHS-F26): диалог называет объект решения — номер, склад, дата. При
   * нескольких черновиках подтверждение опиралось на память о том, какую строку открыли.
   */
  const previewRow = (docsQuery.data?.items ?? []).find((r) => r.id === previewId) as
    | {
        id: string;
        voucher_no?: string | null;
        warehouse_name?: string | null;
        warehouse_id?: string | null;
        created_at: string;
      }
    | undefined;
  const previewTitle = previewRow
    ? m.route_sr_preview_title_named({
        no: previewRow.voucher_no ?? previewRow.id.slice(0, 8),
        warehouse:
          previewRow.warehouse_name ??
          (previewRow.warehouse_id ? (warehouseMap.get(previewRow.warehouse_id) ?? '—') : '—'),
        date: formatDate(previewRow.created_at),
      })
    : m.route_sr_preview_title();
  const previewQuery = useQuery({
    queryKey: ['stock-reconciliation', previewId, 'preview'],
    queryFn: () => previewStockReconciliation(previewId ?? ''),
    enabled: !!previewId,
  });

  const cancelPreview = useQuery({
    queryKey: ['stock-reconciliation', cancelId, 'cancel-preview'],
    queryFn: () => cancelPreviewStockReconciliation(cancelId ?? ''),
    enabled: !!cancelId,
  });

  // B59 / WHS-B14 — кандидаты по строке матрицы `stock_entry_cancel` (той же, что проверяет
  // сервер), без исполнителя; раньше — админский `/admin/users` с фильтром «контролёр или админ».
  const approversQuery = useQuery({
    queryKey: ['stock-entries', 'cancel-approvers', 'stock_reconciliation', cancelId],
    queryFn: () => fetchStockCancelApprovers({ type: 'stock_reconciliation', id: cancelId ?? '' }),
    enabled: !!cancelId,
  });
  /** WHS-B14 (UX, круг 1) — кто согласовал, в тосте успеха. */
  const approverChosen = (approversQuery.data?.items ?? []).find((u) => u.id === cancelApprover);
  const approverName = approverChosen ? (approverChosen.full_name ?? approverChosen.email) : null;

  const actorId = useAuthStore((s) => s.user?.user_id ?? '');
  const actorRole = useAuthStore((s) => (s.user?.role as string | undefined) ?? '');
  /** F53 (WHS-F21): причина отклонения — прямо из предпросмотра, без ухода со страницы. */
  const reasonsMut = useMutation({
    mutationFn: (args: { id: string; line_id: string; variance_reason: string | null }) =>
      setStockReconciliationReasons(args.id, [
        { line_id: args.line_id, variance_reason: args.variance_reason },
      ]),
    meta: { suppressErrorToast: true },
    onSuccess: (_r, args) =>
      qc.invalidateQueries({ queryKey: ['stock-reconciliation', args.id, 'preview'] }),
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_sr_reason_save_failed(),
        description: err.message,
      }),
  });
  const submitMut = useMutation({
    mutationFn: (id: string) => submitStockReconciliation(id, { userId: actorId, role: actorRole }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_sr_toast_submitted() });
      void qc.invalidateQueries({ queryKey: ['stock-reconciliation', 'docs'] });
      setPreviewId(null);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_sr_toast_submit_failed(),
        description: err.message,
      }),
  });

  const cancelMut = useMutation({
    mutationFn: () =>
      cancelStockReconciliation(
        cancelId ?? '',
        cancelReason.trim(),
        { userId: actorId, role: actorRole },
        cancelApprover,
      ),
    onSuccess: () => {
      toast.push({
        variant: 'success',
        title: m.route_sr_toast_cancelled(),
        ...(approverName
          ? { description: m.stock_cancel_toast_approved_by({ name: approverName }) }
          : {}),
      });
      void qc.invalidateQueries({ queryKey: ['stock-reconciliation', 'docs'] });
      setCancelId(null);
      setCancelReason('');
      setCancelApprover('');
      setAcknowledged(false);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_sr_toast_cancel_failed(),
        description: err.message,
      }),
  });

  const previewLines = previewQuery.data?.lines ?? [];
  const hasCriticalLines = previewLines.some(
    (l) => l.severity === 'critical_variance' && l.line_id,
  );
  const missingReasonCount = useMemo(
    () => previewLines.filter((l) => l.reason_required && !l.severity.startsWith('match')).length,
    [previewLines],
  );
  const reasonsMissing = useMemo(
    () => previewLines.some((l) => l.reason_required && !l.severity.startsWith('match')),
    [previewLines],
  );

  const eligibleApprovers = approversQuery.data?.items ?? [];
  const reasonValid = cancelReason.trim().length >= 10;
  const hasCriticalCancel = (cancelPreview.data?.warnings ?? []).some(
    (w) => w.severity === 'danger',
  );

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_stock_reconciliation()}
        description={m.route_sr_list_description()}
        actions={
          <Link to="/stock-reconciliation/new">
            <Button leadingIcon={<Plus size={14} />}>{m.route_sr_list_btn_new()}</Button>
          </Link>
        }
      />

      <ListPageShell
        state={docsState}
        emptyTitle={m.route_sr_list_empty_title()}
        emptyDescription={m.route_sr_list_empty_description()}
        emptyAction={
          <Link to="/stock-reconciliation/new">
            <Button variant="primary" leadingIcon={<Plus size={14} />}>
              {m.route_sr_list_btn_new()}
            </Button>
          </Link>
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_sr_list_aria()}
            data={rows}
            rowKey={(r) => r.id}
            stickyFirstColumn
            columns={[
              {
                id: 'id',
                header: m.common_column_id(),
                sticky: true,
                // EFE-9: link to detail page (SR detail added v0.3.134).
                accessor: (r) => (
                  <Link
                    to="/stock-reconciliation/$id"
                    params={{ id: r.id }}
                    className={shared.accentLink}
                  >
                    {(r as { voucher_no?: string | null }).voucher_no ?? r.id.slice(0, 8)}
                  </Link>
                ),
                mono: true,
              },
              {
                id: 'kind',
                header: m.route_sr_list_col_kind(),
                // FE-91: raw enum (physical_count/variance_adjustment/...) → i18n.
                accessor: (r) => m.sr_kind_label({ kind: r.kind }),
              },
              {
                id: 'wh',
                header: m.route_sr_list_col_warehouse(),
                /**
                 * F4 [WHS-F7] — колонка перестала быть прочерком.
                 *
                 * Склад у большинства документов не лежал в шапке, а в позициях
                 * был всегда — B5 научил сервер выводить его оттуда. Несколько
                 * складов в одном документе — законный случай (пересчёт по
                 * нескольким зонам), и тогда честнее сказать «сколько», чем
                 * назвать первый попавшийся.
                 */
                accessor: (r) => {
                  const row = r as {
                    warehouse_name?: string | null;
                    warehouse_count?: number | null;
                  };
                  if ((row.warehouse_count ?? 0) > 1) {
                    return m.route_sr_warehouse_multi({ count: row.warehouse_count ?? 0 });
                  }
                  return (
                    row.warehouse_name ??
                    (r.warehouse_id ? warehouseMap.get(r.warehouse_id) : null) ??
                    (r.warehouse_id ? r.warehouse_id.slice(0, 8) : '—')
                  );
                },
              },
              {
                id: 'created',
                header: m.route_sr_list_col_created(),
                // FE-95: ISO date → formatDate (DD.MM.YYYY in ru).
                accessor: (r) => formatDate(r.created_at),
              },
              {
                id: 'lines',
                header: m.route_sr_list_col_lines(),
                accessor: (r) => (r.lines ?? []).length,
                align: 'right',
                mono: true,
              },
              {
                id: 'status',
                header: m.route_sr_list_col_status(),
                /* Ширина по содержимому: бэйдж статуса не растягивается. */
                width: '1%',
                // F2: тот же словарь и тот же бейдж, что у складских документов.
                // Здесь была своя карта с подписью «Черновик готов» на проведённом
                // и `blocked` (danger) на отменённом — решение человека читалось
                // как отказ системы.
                accessor: (r) => (
                  <DocStatusBadge status={(r.status ?? 'draft') as DocStatusValue} />
                ),
              },
              {
                /**
                 * Гейт C, раунд 3, UI-4 — ОДНА колонка действий вместо двух.
                 *
                 * «Предпросмотр» и «Отменить» жили в двух отдельных колонках с
                 * пустыми шапками, и между ними лежало ~115 px пустоты: взгляд
                 * вёл строку через провал, а действия строки читались как две
                 * несвязанные вещи. Действия одной строки — одна группа.
                 */
                id: 'actions',
                header: '',
                align: 'right',
                /*
                 * Раунд 4, UI-2 — колонка действий перестала быть
                 * поглотителем ширины.
                 *
                 * Замер: без явной ширины ей доставалось 437 px, кнопки
                 * прижимались вправо, и между статусом и первым действием
                 * лежало ~178 px пустоты — строка читалась через провал.
                 * `1%` в таблице автоматической раскладки сжимает колонку до
                 * содержимого, и лишнее место уходит колонкам с текстом,
                 * которым оно и нужно.
                 */
                width: '1%',
                accessor: (r) => {
                  const previewBlocked = r.status !== 'draft';
                  const isOpening = r.kind === 'opening_stock';
                  const cancelBlocked = r.status !== 'submitted' || isOpening;
                  const cancelReason = isOpening
                    ? m.route_sr_cancel_disabled_opening()
                    : m.route_sr_cancel_disabled_reason();
                  return (
                    <span className={styles.rowActions}>
                      {/* F53 (B22): черновик продолжают, а не только смотрят. */}
                      {r.status === 'draft' && (
                        <Link to="/stock-reconciliation/$id/edit" params={{ id: r.id }}>
                          <Button size="sm" variant="ghost" leadingIcon={<Pencil size={12} />}>
                            {m.route_sr_btn_continue()}
                          </Button>
                        </Link>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        leadingIcon={<Eye size={12} />}
                        /**
                         * Причина достижима с КЛАВИАТУРЫ: атрибут `disabled`
                         * выбрасывает кнопку из порядка обхода, и подсказка
                         * существует только для мыши. `aria-disabled`
                         * оставляет её фокусируемой и объявляет состояние
                         * вслух, а нажатие не проходит первым же условием.
                         */
                        aria-disabled={previewBlocked || undefined}
                        {...(previewBlocked ? { title: m.route_sr_preview_disabled_reason() } : {})}
                        onClick={() => {
                          if (previewBlocked) return;
                          setPreviewId(r.id);
                        }}
                      >
                        {m.route_sr_btn_preview()}
                      </Button>
                      {/*
                        Гейт C, раунд 4 — отмена подана КАК РАЗРУШАЮЩЕЕ
                        действие, а не как соседняя ссылка.
                        Обе половины гейта сошлись: «Отменить» шло тем же
                        приглушённым грейдом, что читающий «Предпросмотр», и в
                        восьми пикселях от него — при том что отменяет документ
                        с 91 строкой регистра и переписывает оценку склада.
                        §4.1: разрушающее — текст и рамка `--danger`.
                      */}
                      {/*
                        F34 (WHS-F34): отмена — только у проведённого. У черновика
                        она стояла приглушённой, у проведённого — красной: одна
                        колонка, два веса для одного действия. Черновику и
                        отменённому отмена неприменима; у начальных остатков она
                        остаётся приглушённой с причиной в подсказке.
                      */}
                      {r.status === 'submitted' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          leadingIcon={<XCircle size={12} />}
                          aria-disabled={cancelBlocked || undefined}
                          {...(cancelBlocked ? { title: cancelReason } : {})}
                          onClick={() => {
                            if (cancelBlocked) return;
                            setCancelId(r.id);
                          }}
                        >
                          {m.route_sr_btn_cancel_doc()}
                        </Button>
                      )}
                    </span>
                  );
                },
              },
            ]}
          />
        )}
      </ListPageShell>

      <Dialog
        open={!!previewId}
        onClose={() => setPreviewId(null)}
        title={previewTitle}
        size="xl"
        footer={
          <div className={styles.footer}>
            {/* F53 (WHS-F23; В-23, владелец 15.09): проведение без согласующего, но
                необратимость и что изменится — словами у самой кнопки. */}
            {previewQuery.data && (
              <p className={styles.irreversibleNote} data-testid="sr-irreversible-note">
                {/* Гейт F59 (B-UI-2/B-UX-2): фраза — справка у решения, а не второй
                    сигнал тревоги; эффект назван числами, а не «изменятся остатки». */}
                <AlertTriangle size={14} aria-hidden="true" className={styles.irreversibleIcon} />
                <span>
                  {m.route_sr_irreversible_effect({
                    count: String(
                      previewQuery.data.summary?.with_variance ??
                        previewQuery.data.summary?.items_affected ??
                        0,
                    ),
                    value: formatMoney(previewQuery.data.summary?.total_variance_value ?? null),
                  })}
                </span>
              </p>
            )}
            {/* Гейт F59 (B-UI-1/B-UX-1): выключенная «Провести» говорит, почему, —
                видимым текстом рядом, а не только подсказкой при наведении. */}
            {previewQuery.data && reasonsMissing && (
              <span className={styles.blockedReason}>
                {m.route_sr_submit_blocked_reasons({ count: String(missingReasonCount) })}
              </span>
            )}
            <Button variant="ghost" onClick={() => setPreviewId(null)}>
              {m.common_button_close()}
            </Button>
            {/*
              Раунд 5 гейта C — проведение НЕДОСТУПНО, пока предпросмотр не
              загружен. Дословно ревьюера: «„Провести“ активна над пустым
              предпросмотром — это Tier 2 write в SLE вслепую». Отказ загрузки
              и «отклонений нет» выглядели одинаково, и кнопка предлагала
              записать в регистр то, чего человек не видел.
            */}
            {/*
              Раунд 11 гейта C — кнопка называет ДЕЙСТВИЕ СИСТЕМЫ, а не команду
              человеку. «Заполните причины» было указанием пользователю в том
              месте, где продукт везде пишет, что произойдёт по нажатию; чего
              не хватает — говорит предупреждение над таблицей и подсказка
              самой кнопки, а не подмена её подписи.

              Недоступность — тем же приёмом, что строкой выше: `aria-disabled`
              вместо `disabled` оставляет кнопку в порядке обхода с клавиатуры,
              и причина отказа доходит до человека, который экран не видит.
            */}
            <Button
              variant="primary"
              disabled={submitMut.isPending}
              aria-disabled={
                previewQuery.isError || previewQuery.isLoading || reasonsMissing || undefined
              }
              {...(previewQuery.isError || previewQuery.isLoading
                ? { title: m.route_sr_submit_blocked_no_preview() }
                : reasonsMissing
                  ? {
                      title: m.route_sr_reasons_missing_alert(),
                      /* Подсказка при наведении существует только для мыши.
                         Причина отказа связывается с кнопкой ссылкой на само
                         предупреждение — тогда её произносит и скринридер. */
                      'aria-describedby': REASONS_ALERT_ID,
                    }
                  : {})}
              loading={submitMut.isPending}
              onClick={() => {
                if (previewQuery.isError || previewQuery.isLoading || reasonsMissing) return;
                if (previewId) submitMut.mutate(previewId);
              }}
            >
              {m.route_sr_submit_btn()}
            </Button>
          </div>
        }
      >
        {previewQuery.data && (
          <>
            <div className={styles.summary}>
              <div>
                <strong>{m.route_sr_summary_items_affected()}</strong>
                <span className={styles.summaryValue}>
                  {previewQuery.data.summary?.items_affected ?? 0}
                </span>
              </div>
              <div>
                <strong>{m.route_sr_summary_total_variance()}</strong>
                <span className={styles.summaryValue}>
                  {formatMoney(previewQuery.data.summary?.total_variance_value ?? null)}
                </span>
              </div>
              <div>
                <strong>{m.route_sr_summary_max_variance_qty()}</strong>
                <span className={styles.summaryValue}>
                  {formatDecimal(maxVarianceOf(previewQuery.data.lines), { maxDecimals: 2 })}
                </span>
              </div>
            </div>
            {/* F53 (WHS-F19): одна формулировка-счётчик, честная при любом K. */}
            {previewQuery.data.summary?.total !== undefined && (
              <p className={styles.previewCount} data-testid="sr-preview-count">
                {m.route_sr_preview_count({
                  shown: String(previewLines.length),
                  total: String(previewQuery.data.summary.total),
                  variance: String(previewQuery.data.summary.with_variance ?? 0),
                })}
              </p>
            )}
            {/* F53 (WHS-F22): провенанс чисел — «ожидается» есть остаток STAITER на дату
                пересчёта, деньги — методом оценки компании. */}
            {previewQuery.data.as_of && (
              <p className={styles.previewProvenance} data-testid="sr-preview-provenance">
                {m.route_sr_preview_provenance({
                  date: formatDate(previewQuery.data.as_of),
                  method: valuationMethodLabel(previewQuery.data.valuation_method),
                })}
              </p>
            )}
            {reasonsMissing && (
              <div
                id={REASONS_ALERT_ID}
                role="alert"
                className={[shared.inlineAlert, shared.inlineAlertWarn, styles.previewAlert].join(
                  ' ',
                )}
              >
                {/* Гейт F59 (B-UX-4, B-UI-3): незаполненная причина — предупреждение, не
                    ошибка; «КРИТИЧЕСКОЕ» в таблице уже красное. */}
                <AlertTriangle size={12} aria-hidden="true" /> {m.route_sr_reasons_missing_alert()}{' '}
                {/* F53 (WHS-F21) + гейт F59 (B-UX-3): причина выбирается в строке таблицы. */}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    const first = document.querySelector<HTMLElement>(
                      '[data-reason-select] button',
                    );
                    first?.scrollIntoView({ block: 'nearest' });
                    first?.focus();
                  }}
                >
                  {m.route_sr_reasons_where()}
                </button>
              </div>
            )}
          </>
        )}
        {/*
          Гейт C, раунд 5 — пустой предпросмотр говорит ОДНОЗНАЧНО.
          Обе половины гейта сошлись: «Нет данных» одинаково читалось как
          «отклонений нет, проводить безопасно» и как «предпросмотр не
          загрузился». На пути записи в регистр это разница между «провести
          спокойно» и «провести вслепую», и различать её обязан экран, а не
          догадка оператора.
        */}
        {/*
          Раунд 12 — раскладка ФИКСИРОВАННАЯ, и это не украшение.

          В раскладке по содержимому колонка обязана вместить свою самую
          широкую ячейку; после того как числа и коды стали неразрывными, сумма
          естественных ширин перевалила за диалог, и таблица уехала в
          горизонтальную прокрутку — «Критичность» вышла за правый край на
          17 px. То есть признак, ради которого экран открывают, оказался за
          кадром: обе половины гейта назвали это блокирующим.

          Фиксированная раскладка меняет причинность: ширину назначает таблица,
          а не содержимое. Шесть колонок объявляют своё значение, «Позиция»
          забирает остаток и обрезается многоточием, если остатка мало. Ни при
          какой длине наименования правый край больше не сдвигается.
        */}
        <Table
          layout="fixed"
          ariaLabel={m.route_sr_preview_lines_aria()}
          loading={previewQuery.isLoading}
          data={previewLines}
          emptyTitle={
            previewQuery.isError
              ? m.route_sr_preview_failed_title()
              : m.route_sr_preview_no_variance_title()
          }
          emptyDescription={
            previewQuery.isError
              ? m.route_sr_preview_failed_desc()
              : m.route_sr_preview_no_variance_desc()
          }
          rowKey={(_, i) => `pre-${i}`}
          columns={[
            {
              id: 'item',
              header: m.route_sr_col_item(),
              /*
               * Раунд 7 гейта C — `mono` СНЯТ, и это была причина обрезки.
               *
               * Флаг остался в наследство от времён, когда в ячейке лежал хеш.
               * С появлением наименования моноширинный шрифт занял ~460 px и
               * выдавил вправо колонку «Критичность» — то самое, ради чего
               * диалог и открывают. Артикул внутри компонента идентичности и
               * так набирается моноширинным; наименование — обычный текст.
               */
              accessor: (l) => (
                <span className={styles.itemCell}>
                  <EntityRefCell name={l.item_label} code={l.item_sku} />
                </span>
              ),
              /*
               * Ширина по доле, а не по содержимому: наименования складских
               * позиций длинные («ПОЛОТНО ОБРАТНОГО ОСМОСА В РУЛОНАХ
               * BW30HRLE-440I»), и без ограничения колонка идентичности
               * выдавливала критичность за край диалога. Замер: 455 px при
               * контейнере 949 — почти половина ширины на одну колонку.
               *
               * `width` на колонке в таблице автоматической раскладки — только
               * пожелание: содержимое, которое не переносится, всё равно
               * растягивает ячейку. Поэтому предел ставится САМОМУ содержимому.
               *
               * Раунд 10: предел поднят и стал НИЖНЕЙ границей поглотителя —
               * лишнюю ширину забирает имя, а не пустая колонка партии. Числовые
               * колонки сжаты по содержимому.
               */
            },
            {
              id: 'batch',
              width: '130px',
              header: m.route_sr_col_batch(),
              // Партия — номер, а не срез идентификатора (раунд 9 гейта C).
              accessor: (l) => l.batch_no ?? '—',
              mono: true,
              nowrap: true,
            },
            {
              id: 'exp',
              width: '112px',
              header: m.route_sr_col_expected(),
              // Гейт C, раунд 3: числа предпросмотра шли СЫРЫМИ строками из
              // Decimal — без разрядов, без локали. Тот же форматтер, что в
              // реестрах: один экран не может говорить на двух языках чисел.
              accessor: (l) => formatDecimal(l.expected_qty),
              mono: true,
              align: 'right',
              nowrap: true,
            },
            {
              id: 'phys',
              width: '90px',
              header: m.route_sr_col_physical(),
              accessor: (l) => formatDecimal(l.counted_qty),
              mono: true,
              align: 'right',
              nowrap: true,
            },
            {
              id: 'var',
              width: '130px',
              header: m.route_sr_col_variance_qty(),
              accessor: (l) => formatDecimal(l.variance_qty),
              mono: true,
              align: 'right',
              nowrap: true,
            },
            {
              id: 'val',
              width: '140px',
              header: m.route_sr_col_variance_value(),
              accessor: (l) => formatMoney(l.variance_value),
              mono: true,
              align: 'right',
              nowrap: true,
            },
            {
              id: 'sev',
              nowrap: true,
              header: m.route_sr_col_severity(),
              /* Ширина по содержимому: чип не должен ни растягиваться, ни
                 переноситься — иначе критичность снова уезжает за край. */
              width: '152px',
              accessor: (l) => (
                // Раунд 6, UI-4: статус кодируется цветом И глифом (§1.3) —
                // дальтонизм не должен стирать критичность.
                <>
                  <span className={styles.severity} data-sev={l.severity}>
                    {l.severity === 'critical_variance' ? (
                      <ShieldAlert size={12} aria-hidden="true" />
                    ) : l.severity === 'variance' ? (
                      <AlertTriangle size={12} aria-hidden="true" />
                    ) : (
                      <Check size={12} aria-hidden="true" />
                    )}
                    {severityLabel(l.severity)}
                  </span>
                  {/* Раунд 13: маркер требования — РЯДОМ с пилюлей. Внутри он
                      добавлял ей вторую семантику: пилюля говорит «насколько
                      критично», звёздочка — «нужна причина», и это разные
                      сообщения. */}
                  {l.reason_required && (
                    <span
                      className={styles.reasonMark}
                      aria-label={m.route_sr_reason_required_hint()}
                      role="img"
                    >
                      *
                    </span>
                  )}
                </>
              ),
            },
            // Гейт F59 (B2-UI-2): причина — своей колонкой, одной строкой с позицией;
            // колонка есть, только когда в черновике есть критические отклонения.
            ...(previewId && hasCriticalLines
              ? [
                  {
                    id: 'reason',
                    width: '184px',
                    header: m.route_sr_col_reason(),
                    accessor: (l: (typeof previewLines)[number]) =>
                      l.severity === 'critical_variance' && l.line_id ? (
                        <div data-reason-select>
                          <Select
                            label={m.route_sr_reason_for({
                              item: l.item_label ?? l.item_sku ?? '',
                            })}
                            hideLabel
                            size="sm"
                            value={l.variance_reason ?? ''}
                            options={varianceReasonOptions()}
                            disabled={reasonsMut.isPending}
                            onChange={(e) =>
                              reasonsMut.mutate({
                                id: previewId,
                                line_id: l.line_id as string,
                                variance_reason: e.target.value || null,
                              })
                            }
                          />
                        </div>
                      ) : (
                        '—'
                      ),
                  },
                ]
              : []),
          ]}
        />
        {/* Раунд 8, UI-4: значение звёздочки названо словами под таблицей —
            подсказка при наведении недостижима с клавиатуры. */}
        {previewLines.some((l) => l.reason_required) && (
          <p className={styles.reasonFootnote}>{m.route_sr_reason_footnote()}</p>
        )}
      </Dialog>

      <Dialog
        open={!!cancelId}
        onClose={() => setCancelId(null)}
        title={m.route_sr_cancel_title()}
        size="lg"
        footer={
          <div className={styles.footer}>
            <Button variant="ghost" onClick={() => setCancelId(null)}>
              {m.common_button_back()}
            </Button>
            <Button
              variant="destructive"
              disabled={
                !reasonValid ||
                !cancelApprover ||
                cancelMut.isPending ||
                (hasCriticalCancel && !acknowledged)
              }
              loading={cancelMut.isPending}
              onClick={() => cancelMut.mutate()}
            >
              {m.route_sr_cancel_confirm()}
            </Button>
          </div>
        }
      >
        <CancelPreviewViewer
          data={cancelPreview.data}
          loading={cancelPreview.isLoading}
          acknowledged={acknowledged}
          onAcknowledgeChange={setAcknowledged}
        />
        <div className={[shared.formGrid2, shared.mt4].join(' ')}>
          {/*
           * WHS-B14, круг 1 гейта (UX): у выбора есть исход «выбрать некого» и «список не
           * загрузился» — экран называет причину и путь, кнопка не остаётся серой молча.
           */}
          <Select
            label={m.route_sr_cancel_approver_label()}
            value={cancelApprover}
            onChange={(e) => setCancelApprover(e.target.value)}
            placeholder={m.route_se_detail_approver_choose()}
            disabled={approversQuery.isLoading}
            options={eligibleApprovers.map((u) => ({
              value: u.id,
              label: m.route_se_detail_approver_role_user({
                name: u.full_name ?? u.email,
                role: getRoleLabel(u.role),
              }),
            }))}
            hint={m.route_se_detail_approver_hint()}
            {...(approversQuery.isError
              ? { error: m.stock_cancel_approvers_load_failed() }
              : approversQuery.isSuccess && eligibleApprovers.length === 0
                ? { error: m.stock_cancel_no_approvers() }
                : {})}
          />
          {approversQuery.isError && (
            <Button variant="ghost" size="sm" onClick={() => void approversQuery.refetch()}>
              {m.common_button_retry()}
            </Button>
          )}
          <Input
            label={m.route_sr_cancel_reason_label()}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            error={
              cancelReason.length > 0 && !reasonValid
                ? m.route_sr_cancel_reason_min_error()
                : undefined
            }
          />
        </div>
      </Dialog>
    </div>
  );
}
